// Grade + status colour helpers. Colours mirror the CSS design tokens so SVG/canvas
// (which can't read CSS vars off-DOM easily) and inline styles stay consistent.

import type { RecordingStatus } from "../api/types";

export const GRADE_COLORS: Record<string, string> = {
  A: "#0e8c5a",
  B: "#4f9e2f",
  C: "#b8780a",
  D: "#d9600a",
  F: "#e5342a",
};

export function gradeColor(grade: string | null | undefined): string {
  if (!grade) return "#7c877f";
  return GRADE_COLORS[grade.toUpperCase()] ?? "#7c877f";
}

/** Derive a letter grade from a 0-100 score (fallback when grade is absent). */
export function scoreToGrade(score: number | null | undefined): string | null {
  if (score == null) return null;
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  return "F";
}

export const STATUS_META: Record<
  RecordingStatus,
  { label: string; color: string; tone: "neutral" | "active" | "warn" | "good" | "bad" }
> = {
  discovered: { label: "Discovered", color: "#7c877f", tone: "neutral" },
  downloading: { label: "Downloading", color: "#0284c7", tone: "active" },
  validating: { label: "Validating", color: "#0284c7", tone: "active" },
  processing: { label: "Processing", color: "#6366f1", tone: "active" },
  pending_review: { label: "Pending review", color: "#b8780a", tone: "warn" },
  approved: { label: "Approved", color: "#0e8c5a", tone: "good" },
  rejected: { label: "Rejected", color: "#e5342a", tone: "bad" },
  failed: { label: "Failed", color: "#e5342a", tone: "bad" },
  potential_fraud: { label: "Potential fraud", color: "#c2410c", tone: "warn" },
  fraud: { label: "Fraud", color: "#9f1239", tone: "bad" },
  superseded: { label: "Cut (superseded)", color: "#7c877f", tone: "neutral" },
};

// Operator-task category colours, keyed by categoryId from the task catalog. The label is a
// short fallback; the badge prefers the recording's own category string when present.
export const CATEGORY_META: Record<string, { label: string; color: string }> = {
  kitchen: { label: "Kitchen", color: "#d9600a" },
  laundry: { label: "Laundry", color: "#0284c7" },
  cleaning: { label: "Cleaning", color: "#0e8c5a" },
  tidying: { label: "Tidying", color: "#7c3aed" },
  car: { label: "Car", color: "#b8780a" },
  yard: { label: "Yard & Garden", color: "#4d7c0f" },
  pets: { label: "Pets", color: "#c026d3" },
  events: { label: "Travel & Events", color: "#6366f1" },
  // Legacy ids stamped into old recordings' metadata before macro categories.
  featured: { label: "Featured", color: "#7c877f" },
  outros: { label: "Other", color: "#7c877f" },
};

export function categoryColor(categoryId: string | null | undefined): string {
  if (!categoryId) return "#7c877f";
  return CATEGORY_META[categoryId]?.color ?? "#7c877f";
}

export const PROCESSING_STATUSES = new Set<RecordingStatus>([
  "discovered",
  "downloading",
  "validating",
  "processing",
]);

/** Statuses a review decision may be written over — mirrors BULK_REVIEWABLE on the api. */
export const REVIEWABLE_STATUSES = new Set<RecordingStatus>([
  "pending_review",
  "approved",
  "rejected",
]);
